import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { Box, Button, Checkbox, FormControlLabel, IconButton, Tooltip } from '@mui/material'
import withStyles from '@mui/styles/withStyles'
import React from 'react'
import Utilities from '../classes/Utilities'
import AddressForm from '../components/AddressForm'
import { withAppContext } from '../context/AppContextProvider'

const styles = (theme) => ({
    iframe: {
        height: 200,
        width: '100%',
    },
    saveCard: {
        marginRight: 0,
    },
    saveCardInfo: {
        padding: 8,
    },
})

class ExpressFrame extends React.Component {
    iframeRef = React.createRef()

    state = {
        billing: null,
        expressUrl: null,
        paymentSaveCard: this.props.paymentSaveCard,
    }

    componentDidMount() {
        window.addEventListener('message', this.handlePostMessage)
    }

    componentWillUnmount() {
        window.removeEventListener('message', this.handlePostMessage)
    }

    handleAddressSubmit = async (event) => {
        event.preventDefault()

        try {
            const billing = Utilities.getFormValues(event.target)

            const { expressUrl } = await this.props.socket.send('expressPayment', {
                step: 'prepareTransaction',
                billing,
                saveCard: this.state.paymentSaveCard,
            })

            this.setState({
                billing,
                expressUrl,
            })
        } catch (err) {
            this.props.showError(err)
        }
    }

    handlePostMessage = async (event) => {
        const { paymentSaveCard } = this.state
        const { source, data } = event

        try {
            if (source != this.iframeRef.current.contentWindow || data.wylie_script != 'q2pm') {
                return // not from our iframe & script
            }
        } catch {
            return // fallback in case the check above causes an issue itself
        }

        // console.log(data)

        try {
            // check for platform errors
            if ('ExpressResponseCode' in data && data.ExpressResponseCode != '0') {
                throw new Error(`WorldPay / Express error: ${data.ExpressResponseMessage}`)
            }

            // check for card-related errors
            if (
                ('HostedPaymentStatus' in data && data.HostedPaymentStatus != 'Complete') ||
                ('CVVResponseCode' in data && ['M', 'P'].includes(data.CVVResponseCode) == false) ||
                ('AVSResponseCode' in data && ['D', 'E', 'F', 'G', 'I', 'M', 'S', 'U', 'X', 'Y'].includes(data.AVSResponseCode) == false)
            ) {
                // if AVS failed due to incompatible format or a timeout then let the user know
                if ('AVSResponseCode' in data) {
                    switch (data.AVSResponseCode) {
                        case 'B': throw new Error(`Invalid zip/postal code format`)
                        case 'C': throw new Error(`Invalid street address and zip/postal code format`)
                        case 'P': throw new Error(`Invalid street address format`)
                        case 'R': throw new Error(`Address verification service unavailable or timed-out. Please try again.`)
                    }
                }

                // otherwise, don't be specific about the issue (to help reduce fraud / card probing)
                throw new Error(`Unable to complete transaction, please try again.`)
            }

            // generate a token, which will either be charged or stored on-file
            const { token, expiry, last4, type, transid } = await this.props.socket.send('expressPayment', {
                step: 'generateToken',
                expressTransactionId: data.TransactionID,
            })

            this.props.onTokenize(token, expiry, last4, type, { ourTransactionId: transid, networkTransactionId: data.NetTranID, entryType: 'ecommerce', isCof: paymentSaveCard }, paymentSaveCard)

        } catch (err) {
            this.props.showError(err)

            // re-render the iframe
            try {
                const { expressUrl } = await this.props.socket.send('expressPayment', {
                    step: 'prepareTransaction',
                    billing: this.state.billing,
                    saveCard: this.state.paymentSaveCard,
                })

                this.setState({
                    expressUrl,
                })
            } catch (err) {
                this.props.showError(err)
            }
        }
    }

    handlePaymentSaveChange = () => {
        const { paymentSaveCard } = this.state

        this.setState({
            paymentSaveCard: !paymentSaveCard,
        })
    }

    render() {
        const { className, classes, billTo, forceSaveCard, saveCardLabel } = this.props
        const { expressUrl, paymentSaveCard } = this.state

        if (expressUrl) {
            return (
                <Box className={className}>
                    <iframe
                        ref={this.iframeRef}
                        className={classes.iframe}
                        src={expressUrl}
                        frameBorder="0"
                        scrolling="yes"
                    ></iframe>
                    <br />
                </Box>
            )
        }

        return (
            <Box className={className}>
                <AddressForm
                    excludes={{
                        email: true,
                        phone: true,
                    }}
                    defaults={{
                        name: billTo?.name,
                        street1: billTo?.street1,
                        street2: billTo?.street2,
                        city: billTo?.city,
                        state: billTo?.state,
                        zip: billTo?.zip,
                    }}
                    onSubmit={this.handleAddressSubmit}
                    submitButton={<>
                        <Tooltip disableInteractive arrow title="Cards are securely stored as encrypted tokens. Your actual card number is never saved.">
                            <FormControlLabel
                                className={classes.saveCard}
                                disabled={forceSaveCard}
                                control={
                                    <Checkbox
                                        color="primary"
                                        checked={paymentSaveCard}
                                        onClick={this.handlePaymentSaveChange}
                                    />
                                }
                                label={saveCardLabel || 'Save card on file (secure)'}
                            />
                        </Tooltip>
                        <Tooltip disableInteractive arrow title="Cards are securely stored as encrypted tokens. Your actual card number is never saved." enterTouchDelay={0} leaveTouchDelay={10000}>
                            <IconButton className={classes.saveCardInfo} size="large">
                                <HelpOutlineIcon />
                            </IconButton>
                        </Tooltip><br />
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                        >
                            Enter Card Number
                        </Button>
                    </>}
                />
            </Box>
        )
    }
}

export default withAppContext(withStyles(styles, { withTheme: true })(ExpressFrame))
